/**
 * Country to Currency Mapping
 * Maps country names to their primary currency codes
 * Used for auto-selecting currency based on country selection
 */

const COUNTRY_TO_CURRENCY = {
  // North America
  'united states': 'USD',
  'usa': 'USD',
  'us': 'USD',
  'canada': 'CAD',
  'mexico': 'MXN',

  // Europe
  'united kingdom': 'GBP',
  'uk': 'GBP',
  'england': 'GBP',
  'scotland': 'GBP',
  'wales': 'GBP',
  'germany': 'EUR',
  'france': 'EUR',
  'italy': 'EUR',
  'spain': 'EUR',
  'netherlands': 'EUR',
  'belgium': 'EUR',
  'austria': 'EUR',
  'ireland': 'EUR',
  'portugal': 'EUR',
  'greece': 'EUR',
  'finland': 'EUR',
  'luxembourg': 'EUR',
  'switzerland': 'CHF',
  'sweden': 'SEK',
  'norway': 'NOK',
  'denmark': 'DKK',
  'poland': 'PLN',
  'czech republic': 'CZK',
  'czechia': 'CZK',
  'hungary': 'HUF',
  'romania': 'RON',
  'russia': 'RUB',
  'ukraine': 'UAH',
  'turkey': 'TRY',

  // Asia
  'japan': 'JPY',
  'china': 'CNY',
  'south korea': 'KRW',
  'korea': 'KRW',
  'india': 'INR',
  'thailand': 'THB',
  'vietnam': 'VND',
  'indonesia': 'IDR',
  'malaysia': 'MYR',
  'singapore': 'SGD',
  'philippines': 'PHP',
  'hong kong': 'HKD',
  'taiwan': 'TWD',
  'pakistan': 'PKR',
  'bangladesh': 'BDT',
  'sri lanka': 'LKR',
  'nepal': 'NPR',
  'cambodia': 'USD', // USD widely used
  'myanmar': 'MMK',
  'laos': 'LAK',

  // Middle East
  'united arab emirates': 'AED',
  'uae': 'AED',
  'dubai': 'AED',
  'saudi arabia': 'SAR',
  'qatar': 'QAR',
  'kuwait': 'KWD',
  'bahrain': 'BHD',
  'oman': 'OMR',
  'israel': 'ILS',
  'jordan': 'JOD',
  'lebanon': 'USD', // USD widely used due to currency crisis
  'egypt': 'EGP',
  'iraq': 'IQD',
  'iran': 'IRR',

  // Africa
  'south africa': 'ZAR',
  'nigeria': 'NGN',
  'kenya': 'KES',
  'ghana': 'GHS',
  'tanzania': 'TZS',
  'uganda': 'UGX',
  'ethiopia': 'ETB',
  'morocco': 'MAD',
  'tunisia': 'TND',
  'algeria': 'DZD',

  // Oceania
  'australia': 'AUD',
  'new zealand': 'NZD',

  // South America
  'brazil': 'BRL',
  'argentina': 'ARS',
  'chile': 'CLP',
  'colombia': 'COP',
  'peru': 'PEN',
  'venezuela': 'USD', // USD widely used
  'ecuador': 'USD', // Uses USD
  'uruguay': 'UYU',
  'paraguay': 'PYG',
  'bolivia': 'BOB',

  // Central America & Caribbean
  'panama': 'USD', // Uses USD
  'costa rica': 'CRC',
  'guatemala': 'GTQ',
  'honduras': 'HNL',
  'el salvador': 'USD', // Uses USD
  'nicaragua': 'NIO',
  'jamaica': 'JMD',
  'dominican republic': 'DOP',
  'puerto rico': 'USD',
  'cuba': 'CUP',
};

/**
 * Get currency code for a country
 * @param {string} country - Country name
 * @returns {string|null} - Currency code or null if not found
 */
export const getCurrencyForCountry = (country) => {
  if (!country) return null;
  
  const normalizedCountry = country.toLowerCase().trim();
  return COUNTRY_TO_CURRENCY[normalizedCountry] || null;
};

/**
 * List of supported currencies for dropdowns
 * Expanded to include more currencies commonly used by expats
 */
export const SUPPORTED_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'Mex$' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
];

export default COUNTRY_TO_CURRENCY;
